import _ from 'lodash';
import React, { Dispatch, SetStateAction, useState } from 'react';
import MultiProjectTagGroupAutocomplete from '../../../components/muiLegacy/Autocomplete/MultiProjectTagGroupAutocomplete';
import MultiTagGroupAutocomplete from '../../../components/muiLegacy/Autocomplete/MultiTagGroupAutocomplete';
import { AnalysisIntervalsPopover } from '../../../components/muiLegacy/Popover/AnalysisIntervalsPopover';
import RankIntervalRadios from '../../../components/muiLegacy/Radios/rankIntervalRadios';
import AverageCTRMultiSelect from '../../../components/muiLegacy/Select/AverageCTRMultiSelect';
import VoxelInput from '../../../components/voxel/Forms/VoxelInput';
import VoxelSelect, { GenericSelectOption } from '../../../components/voxel/Forms/VoxelSelect';
import VoxelSwitch from '../../../components/voxel/Forms/VoxelSwitch';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import VoxelTooltipShort from '../../../components/voxel/Tooltip/VoxelTooltipShort';
import { currencyList } from '../../../graphql/projects/models/Currencies';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { formatOnlyAllowNumbers } from '../../../logic/otherUtilities/formatting/formatOnlyAllowNumbers';
import SingleTagAutocomplete from '../components/Autocomplete/SingleTagAutocomplete';
import SingleTagGroupAutocomplete from '../components/Autocomplete/SingleTagGroupAutocomplete';
import { AnalysisFilterState } from './analysisFilterState';
import VoxelAutocompleteDomainMulti from '../../../components/muiLegacy/Autocomplete/VoxelAutocompleteDomainMulti';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import { DomainTypeGq } from '../../../graphql/projects/models/DomainTypeGq';

export interface AnalysisFilterPanelProps {
  project: ProjectGq;
  analysisFilterState: AnalysisFilterState;
  setAnalysisFilterState: Dispatch<SetStateAction<AnalysisFilterState>>;
  reload: () => Promise<any>;
}

export const AnalysisFilterPanel: React.FC<AnalysisFilterPanelProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  reload
}) => {

  const currencySymbol = currencyList.find((c) => c.code === project.currency).symbol;
  const monthArrayOptions: GenericSelectOption[] = _.range(6, 25, 6).map((v) => ({
    value: v.valueOf(),
    title: `${v.toString()} ${v === 1 ? 'month' : 'months'}`,
  }));
  const yearArrayOptions: GenericSelectOption[] = [
    { title: '1 year', value: 1 },
    { title: '2 years', value: 2 },
    { title: '3 years', value: 3 },
  ];

  const nrOfItemsOptions: GenericSelectOption[] = [
    { title: 'Top 7', value: 7 },
    { title: 'Top 10', value: 10 },
    { title: 'Top 15', value: 15 },
    { title: 'Top 20', value: 20 },
    { title: 'Top 30', value: 30 },
    { title: 'Top 40', value: 40 },
    { title: 'Top 50', value: 50 },
  ];

  const rankingList: GenericSelectOption[] = _.range(1, 11, 1).map((v) => ({
    value: v.valueOf(),
    title: `${v.toString()}`,
  }));

  // Use automatic domains switch
  const [useAutomaticDomainsFE, setUseAutomaticDomainsFE] = useState(project.useAutomaticDomains);
  const updateUseAutomaticMutation = projectsApiService.mutations.projects.useUpdateProjectUseAutomatic();
  const handleUpdateUseAutomatic = (useAutomatic: boolean) => {
    setUseAutomaticDomainsFE(useAutomatic);
    setAnalysisFilterState(prev => ({ ...prev, domainIds: prev.domainIds.filter(d => project.domains.some(pd => pd.id === d && (useAutomatic || pd.type !== DomainTypeGq.AUTOMATIC))) }))
    updateUseAutomaticMutation({
      id: project.id,
      useAutomaticDomains: useAutomatic
    }).then(() => reload());
  };

  return (
    <div className={'mx-1 mt-2 flex flex-row items-center gap-x-2 flex-wrap'}>
      <VoxelText variant="title3" paddingBot={false} paddingTop={false}>
        Filters
      </VoxelText>
      <div className={'flex flex-row items-center gap-x-3 gap-y-3 flex-wrap'}>
        {analysisFilterState?.shownFields.showSerpOnly && (
          <div className='mx-12'>
            <VoxelTooltipShort message="If enabled, only keywords with SERP data collected are shown. This is for consistency when comparing with performance charts, which only show SERP data.">
              <VoxelSwitch
                color="primary"
                size="md"
                title={'SERP only'}
                value={analysisFilterState?.serpOnly}
                onChange={(value: boolean) =>
                  setAnalysisFilterState((prev) => ({ ...prev, serpOnly: value }))
                }
              />
            </VoxelTooltipShort>
          </div>
        )}
        {analysisFilterState?.shownFields.showDomainIds && (
          <div className='mx-12 '>
            <div className='flex flex-row items-center gap-2'>
              <VoxelAutocompleteDomainMulti
                domainIds={analysisFilterState.domainIds}
                setDomainIds={(domainIds: number[]) => setAnalysisFilterState((prev) => ({ ...prev, domainIds }))}
                project={project}
                label="Domains"
                labelPosition="left"
                disabled={!project.domains.length}
                inputWidthClass="w-64"
                size="sm"
                maxHeightOptions={400}
                bottomElements={
                  [<div className='w-full flex flex-row justify-between'>
                    <VoxelText variant='form-caption'>
                      Include automatic domains
                    </VoxelText>
                    <VoxelSwitch
                      size='sm'
                      value={useAutomaticDomainsFE}
                      onChange={() => handleUpdateUseAutomatic(!useAutomaticDomainsFE)}
                    />
                  </div>,
                  <div className='w-full flex flex-row justify-between'>
                    <VoxelText variant='form-caption' disabled>
                      Other automatic domain color
                    </VoxelText>
                    <VoxelSwitch
                      size='sm'
                      value={analysisFilterState?.competitorColor && project.useAutomaticDomains}
                      onChange={(value: boolean) =>
                        setAnalysisFilterState((prev) => ({ ...prev, competitorColor: value }))
                      }
                    />
                  </div>
                  ]}
              />
            </div>
          </div>
        )}
        {analysisFilterState?.shownFields.showTagGroupId && (
          <div className='mx-12'>
            <SingleTagGroupAutocomplete
              tagGroups={project.tagGroups}
              analysisFilterState={analysisFilterState}
              setAnalysisFilterState={setAnalysisFilterState}
            />
          </div>
        )}
        {analysisFilterState?.shownFields.showTagId && (
          <div className='mx-12'>
            <SingleTagAutocomplete
              tagGroup={project.tagGroups?.find((t) => t.id === analysisFilterState.tagGroupId)}
              analysisFilterState={analysisFilterState}
              setAnalysisFilterState={setAnalysisFilterState}
            />
          </div>
        )}
        {analysisFilterState?.shownFields.showHBLt && (
          <div className='mx-12'>
            <AnalysisIntervalsPopover
              analysisFilterState={analysisFilterState}
              setAnalysisFilterState={setAnalysisFilterState}
            />
          </div>
        )}
        {analysisFilterState?.shownFields.showLimitItems && (
          <div className='mx-12'>
            <VoxelSelect
              label="Shown items"
              labelPosition="left"
              outlined
              options={nrOfItemsOptions}
              selectedOptionValue={analysisFilterState?.limitItems}
              setSelectedOption={(option) => {
                setAnalysisFilterState((prev) => ({
                  ...prev,
                  limitItems: nrOfItemsOptions.find((item) => item.value === option.value)
                    .value,
                }));
              }}
              size="sm"
              inputWidthClass="w-28"
            />
          </div>
        )}
        {analysisFilterState?.shownFields.showYears && (
          <div className='mx-12'>
            <VoxelSelect
              label="Time span"
              labelPosition="left"
              outlined
              options={yearArrayOptions}
              selectedOptionValue={analysisFilterState?.years}
              setSelectedOption={(option) => {
                setAnalysisFilterState((prev) => ({
                  ...prev,
                  years: yearArrayOptions.find((item) => item.value === option.value).value,
                }));
              }}
              size="sm"
              inputWidthClass="w-28"
            />
          </div>
        )}

        {analysisFilterState?.shownFields.showTagGroupIds && (
          <div className="mx-12 min-w-[300px]">
            <MultiTagGroupAutocomplete
              tagGroups={project.tagGroups}
              analysisFilterState={analysisFilterState}
              setAnalysisFilterState={setAnalysisFilterState}
            />
          </div>
        )}
        {analysisFilterState?.shownFields.multiTagGroupNames && (
          <div className="mx-12  min-w-[300px]">
            <MultiProjectTagGroupAutocomplete
              projectId={project.id}
              analysisFilterState={analysisFilterState}
              setAnalysisFilterState={setAnalysisFilterState}
            />
          </div>
        )}
        {analysisFilterState?.shownFields.showRankSumIntervals && (
          <div className={'mx-12 '}>
            <RankIntervalRadios
              analysisFilterState={analysisFilterState}
              setAnalysisFilterState={setAnalysisFilterState}
            />
          </div>
        )}
        {analysisFilterState?.shownFields.showCurrentResult && (
          <div className={'mx-12 '}>
            <VoxelTooltipShort message="also show results based on client's current traffic">
              <VoxelSwitch
                color="primary"
                title={'Show current estimate'}
                value={analysisFilterState?.currentResult}
                onChange={(value: boolean) =>
                  setAnalysisFilterState((prev) => ({ ...prev, currentResult: value }))
                }
              />
            </VoxelTooltipShort>
          </div>
        )}
        {analysisFilterState?.shownFields.showTargetRankValues && (
          <div className={'mx-12'}>
            <AverageCTRMultiSelect
              label={'Target CTR'}
              labelPosition="left"
              outlined
              setSelectedOptions={(value: GenericSelectOption[]) => {
                setAnalysisFilterState((prev) => ({
                  ...prev,
                  targetRankValues: value.flatMap((t) => t.value),
                }));
              }}
              selectedOptions={analysisFilterState.targetRankValues.map((t) => ({
                title: t.toString(),
                value: t,
              }))}
              options={rankingList}
              size="sm"
              width="w-28"
            />
          </div>
        )}
        {analysisFilterState?.shownFields.showStartMonthIncrement && (
          <div className={'mx-12'}>
            <VoxelTooltipShort message="The ramp up start decides when the strategy and CTR change will begin">
              <VoxelSelect
                label="Ramp up start"
                labelPosition="left"
                outlined
                options={[
                  { value: 1, title: '1st month' },
                  { value: 2, title: '2nd month' },
                  { value: 3, title: '3rd month' },
                  { value: 4, title: '4th month' },
                  { value: 5, title: '5th month' },
                  { value: 6, title: '6th month' },
                ]}
                selectedOptionValue={analysisFilterState?.startMonthIncrement}
                setSelectedOption={(option) => {
                  setAnalysisFilterState((prev) => ({
                    ...prev,
                    startMonthIncrement: option.value ?? 1,
                  }));
                }}
                size="sm"
                inputWidthClass="w-28"
              />
            </VoxelTooltipShort>
          </div>
        )}
        {analysisFilterState?.shownFields.showRampUpTime && (
          <div className={'mx-12'}>
            <VoxelTooltipShort message="The ramp up time is the estimated time it will take for the strategy to reach its full potential">
              <VoxelSelect
                label="Ramp up time"
                labelPosition="left"
                outlined
                options={monthArrayOptions}
                selectedOptionValue={analysisFilterState?.rampUpTime}
                setSelectedOption={(option) => {
                  setAnalysisFilterState((prev) => ({
                    ...prev,
                    rampUpTime: monthArrayOptions.find((item) => item.value === option.value)
                      .value,
                  }));
                }}
                size="sm"
                inputWidthClass="w-28"
              />
            </VoxelTooltipShort>
          </div>
        )}
        {analysisFilterState?.shownFields.showConversionRate && (
          <div className={'mx-12'}>
            <VoxelTooltipShort message="The conversion rate is the percentage of website visiotors who buy something on the site">
              <VoxelInput
                label="Conversion rate"
                value={analysisFilterState?.conversionRate}
                onChange={(event) => {
                  var value = formatOnlyAllowNumbers(event.target.value);
                  setAnalysisFilterState((prev) => ({ ...prev, conversionRate: value }));
                }}
                labelPosition="left"
                size="sm"
                inputWidthClass="w-16"
                endIcon={<>%</>}
                placeholder="0"
              />
            </VoxelTooltipShort>
          </div>
        )}
        {analysisFilterState?.shownFields.showAverageOrderValue && (
          <div className={'mx-12'}>
            <VoxelTooltipShort message="Average order value (AOV) is the average amount for which a website visitor places an order">
              <VoxelInput
                label="Average order value"
                value={analysisFilterState?.averageOrderValue}
                onChange={(event) => {
                  var value = formatOnlyAllowNumbers(event.target.value);
                  setAnalysisFilterState((prev) => ({ ...prev, averageOrderValue: value }));
                }}
                placeholder="0"
                labelPosition="left"
                size="sm"
                inputWidthClass="w-28"
                startIcon={currencySymbol !== 'kr' && <>{currencySymbol}</>}
                endIcon={currencySymbol === 'kr' && <>kr</>}
              />
            </VoxelTooltipShort>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisFilterPanel;
